/**
 * CraneRecoveryExpandedPanel.jsx
 *
 * Progressive phase UI for crane & recovery service request cards.
 * Imported by ServiceRequestCard and rendered instead of GenericExpandedPanel
 * when serviceType === "crane" || "recovery".
 *
 * Phases:
 *   1. Incident details   (read-only, from bot or admin creation)
 *   2. Owner/driver info  (editable — NEW → DETAILS_PENDING)
 *   3. Vehicle selection  (hydra_crane / recovery_non_flatbed / recovery_flatbed + size)
 *   4. Assign vendor      (navigate to vendor page) + assign vehicle + dispatched driver
 *   5. Dispatch & track   (EN_ROUTE → REACHED → DONE)
 */

import { useState, useEffect, useMemo, useRef } from "react";
import "./CraneRecoveryExpandedPanel.css";
import {
  useUpdateQSDetails,
  useUpdateQSVehicleSelection,
  useRouteDistance,
  useUpdateQSCharges,
  useAdminConfirmPayment,
  useAdminConfirmInspection,
  useAdminConfirmOTP,
  useAdminConfirmDone,
  useAssignQSVehicle,
  useUpdateQSStatus,
  useVendorVehicles,
  useFetchAndSaveVehicle,
  useAdminConfirmReached,
  useUpdateQSPhotos,
  useDeleteQSPhoto,
} from "../../../hooks/useCraneQueries.js";
import {useNavigate} from "react-router-dom";

// ─── Status ordering ──────────────────────────────────────────────────────────

const STATUS_ORDER = [
  "NEW",
  "DETAILS_PENDING",
  "VEHICLE_SELECTED",
  "ASSIGNED",
  "MOVED", 
  "REACHED_AT_PICKUP", 
  "VEHICLE_INSPECTED",
  "OTP_VERIFIED",
  "WORKING",
  "DONE", 
  "CANCELLED",
];

function hasReached(current, target) {
  return STATUS_ORDER.indexOf(current) >= STATUS_ORDER.indexOf(target);
}

// ─── Label maps ───────────────────────────────────────────────────────────────

const VEHICLE_TYPE_OPTIONS = [
  { value: "hydra_crane",          label: "Hydra crane"              },
  { value: "recovery_non_flatbed", label: "Recovery van (non-flatbed)" },
  { value: "recovery_flatbed",     label: "Recovery van (flatbed)"   },
];

const VEHICLE_SIZE_OPTIONS = [
  { value: "mini",   label: "Mini"   },
  { value: "medium", label: "Medium" },
  { value: "large",  label: "Large"  },
];

const STATUS_LABEL = {
  NEW:              "New",
  DETAILS_PENDING:  "Details pending",
  VEHICLE_SELECTED: "Vehicle selected",
  ASSIGNED:         "Assigned",
  EN_ROUTE:         "En route",
  REACHED:          "Reached",
  DONE:             "Done",
  CANCELLED:        "Cancelled",
};

const PAYMENT_MODE_LABEL = {
  CASH:      "Cash",
  ONLINE:    "Online transfer",
  UPI:       "UPI",
  CHEQUE:    "Cheque",
  RECEIVED:  "Payment received",
  PAY_LATER: "Pay later",
};

function apiMsg(error, fallback = "Something went wrong. Please try again.") {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error   ||
    error?.message                 ||
    fallback
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CraneRecoveryExpandedPanel({ request, onNavigateVendors, onTrack }) {
  const raw         = request._raw || request;
  const requestCode = raw.requestCode;   // QS-CRANE-001 / QS-RCV-001
  const status      = raw.status;
  const isDone = status === "DONE" || status === "CANCELLED";

  return (
    <div className="cr-pipeline">

      {/* ── Phase 1: Incident details (read-only) ── */}
      <CRPhase
        number={1}
        title="Incident details"
        state={hasReached(status, "DETAILS_PENDING") ? "done" : "active"}
        defaultOpen={status === "NEW"}
        badge={raw.vehicleNumber || "—"}
      >
        <IncidentDetails raw={raw} requestCode={requestCode}/>
      </CRPhase>

      {/* ── Phase 2: Owner & driver info ── */}
      <Phase2OwnerDriver
        requestCode={requestCode}
        raw={raw}
        status={status}
      />

      {/* ── Phase 3: Support vehicle selection ── */}
      <Phase3VehicleSelection
        requestCode={requestCode}
        raw={raw}
        status={status}
      />

      {/* ── Phase 4: Vendor + vehicle assignment ── */}
      <Phase4Assignment
        requestCode={requestCode}
        raw={raw}
        status={status}
        onNavigateVendors={onNavigateVendors}
      />

      {/* ── Phase 4.5: Payment confirmation ── */}
        <Phase4bPayment
          requestCode={requestCode}
          raw={raw}
          status={status}
        />

      {/* ── Phase 5: Dispatch & live track ── */}
      <Phase5Dispatch
        requestCode={requestCode}
        raw={raw}
        status={status}
        onTrack={onTrack}
      />

      {/* ── Cancel ── */}
      {!isDone && (
        <div className="qs-expanded-actions">
          <CancelButton requestCode={requestCode} />
        </div>
      )}
    </div>
  );
}
function FareCalculator({ requestCode, raw }) {
  const {
    data:      routeData,
    isLoading: loadingKm,
    isError:   kmError,
  } = useRouteDistance(
    requestCode,
    !!(raw.pickupLat && raw.pickupLng && raw.dropLat && raw.dropLng)
  );

  const baseKm  = routeData?.km      ?? null;
  const minutes = routeData?.minutes ?? null;
  const noCoords = !(raw.pickupLat && raw.pickupLng && raw.dropLat && raw.dropLng);

  const mutation = useUpdateQSCharges(requestCode);
  const isSaving = mutation.isPending;

  // ── State — seeded once from saved values, never re-synced from raw ────────
  const [variation, setVariation] = useState(
    raw.kmVariationPct != null ? String(raw.kmVariationPct) : "0"
  );
  const [trips,  setTrips]  = useState(raw.trips       ? Number(raw.trips)       : 2);
  const [rate,   setRate]   = useState(raw.rate_per_km ? String(raw.rate_per_km) : "");
  const [amount, setAmount] = useState(raw.charges     ? String(raw.charges)     : "");
  const [saved,  setSaved]  = useState(!!(raw.charges));

  // ── Derived km values ──────────────────────────────────────────────────────
  const adjKm = useMemo(() => {
    if (baseKm == null) return null;
    const pct = parseFloat(variation) || 0;
    return parseFloat((baseKm * (1 + pct / 100)).toFixed(2));
  }, [baseKm, variation]);

  const totalKm = useMemo(() => {
    if (adjKm == null) return null;
    return parseFloat((adjKm * trips).toFixed(2));
  }, [adjKm, trips]);

  const baseKmStr  = baseKm  != null ? baseKm.toFixed(1)  : null;
  const adjKmStr   = adjKm   != null ? adjKm.toFixed(1)   : null;
  const totalKmStr = totalKm != null ? totalKm.toFixed(1) : null;

  // ── Duration ───────────────────────────────────────────────────────────────
  const formatDuration = (mins) => {
    if (mins == null) return null;
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
  };
  const durationStr = formatDuration(minutes);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleVariationChange = (val) => {
    const clamped = Math.min(99, Math.max(0, parseInt(val) || 0));
    setVariation(String(clamped));
    if (baseKm == null) return;
    const newAdjKm   = parseFloat((baseKm * (1 + clamped / 100)).toFixed(2));
    const newTotalKm = parseFloat((newAdjKm * trips).toFixed(2));
    if (rate) {
      const r = parseFloat(rate);
      if (!isNaN(r) && r > 0) setAmount(String(Math.round(r * newTotalKm)));
    } else if (amount) {
      const a = parseFloat(amount);
      if (!isNaN(a) && a > 0) setRate((a / newTotalKm).toFixed(2));
    }
  };

  const handleTripsChange = (val) => {
  setTrips(val);
  if (adjKm == null) return;
  const newTotalKm = parseFloat((adjKm * val).toFixed(2));
  // Always re-derive amount from rate when toggling trips
  if (rate) {
    const r = parseFloat(rate);
    if (!isNaN(r) && r > 0) setAmount(String(Math.round(r * newTotalKm)));
  } else if (amount) {
    // amount currently holds the displayed value (already multiplied if trips was 2)
    // strip back to one-way base, then re-apply new multiplier
    const currentTrips = trips; // still old value since setTrips is async
    const oneWayAmt = parseFloat(amount) / currentTrips;
    setAmount(String(Math.round(oneWayAmt * val)));
    if (adjKm > 0) setRate((oneWayAmt / adjKm).toFixed(2));
  }
};

  const handleRateChange = (val) => {
  setRate(val);
  const r = parseFloat(val);
  if (!isNaN(r) && r > 0 && totalKm) setAmount(String(Math.round(r * totalKm)));
  else setAmount("");
};

  const handleAmountChange = (val) => {
  // val is what admin types — treat as one-way amount, multiply by trips
  const oneWay = parseFloat(val);
  if (!isNaN(oneWay) && oneWay > 0) {
    const total = Math.round(oneWay * trips);
    setAmount(String(total));
    if (totalKm) setRate((total / totalKm).toFixed(2));
  } else {
    setAmount(val);   // let them clear/type freely
    setRate("");
  }
};

  const handleSave = async () => {
    const charges   = parseFloat(amount);
    const ratePerKm = parseFloat(rate);
    if (!charges || charges <= 0) return;
    await mutation.mutateAsync({
      ratePerKm:      ratePerKm || null,
      charges,
      kmVariationPct: parseFloat(variation) || 0,
      trips,
    });
    setSaved(true);
  };

  // ── Distance badge ─────────────────────────────────────────────────────────
  const distanceBadge = () => {
    if (noCoords) {
      return (
        <span className="cr-fare-no-coords">
          No coordinates — enter amount manually
        </span>
      );
    }
    if (loadingKm) {
      return (
        <div className="cr-fare-dist-badge cr-fare-dist-badge--loading">
          <span className="cr-plate-spinner" style={{ width: 10, height: 10 }} />
          <span className="cr-fare-dist-unit">Fetching route…</span>
        </div>
      );
    }
    if (kmError) {
      return (
        <span className="cr-fare-no-coords cr-fare-no-coords--error">
          Route unavailable — enter amount manually
        </span>
      );
    }
    return (
      <div className="cr-fare-badges">
        <div className="cr-fare-dist-badge">
          <span className="cr-fare-dist-num">{baseKmStr}</span>
          <span className="cr-fare-dist-unit">km by road</span>
        </div>
        {durationStr && (
          <div className="cr-fare-dist-badge cr-fare-dist-badge--time">
            <span className="cr-fare-dist-num">{durationStr}</span>
            <span className="cr-fare-dist-unit">est. time</span>
          </div>
        )}
      </div>
    );
  };

  const kmReady = !noCoords && !loadingKm && !kmError && baseKm != null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cr-fare-calc">

      {/* Header */}
      <div className="cr-fare-header">
        <span className="cr-fare-title">Fare calculator</span>
        {distanceBadge()}
      </div>

      {/* Route hint */}
      {kmReady && raw.pickupLocation && raw.dropLocation && (
        <div className="cr-fare-route">
          <span className="cr-fare-route-dot" />
          <span className="cr-fare-route-text">
            {raw.pickupLocation} → {raw.dropLocation}
          </span>
        </div>
      )}

      {/* ── Saved state ── */}
      {saved ? (
        <div className="cr-fare-saved-banner">
          <CheckSmIcon />
          <div className="cr-fare-saved-body">
            <div className="cr-fare-saved-title">Charges set</div>
            <div className="cr-fare-saved-sub">
              ₹{Number(amount).toLocaleString("en-IN")}
              {rate && totalKmStr
                ? ` = ₹${parseFloat(rate).toFixed(2)}/km × ${
                    trips === 2
                      ? `${adjKmStr} km × 2 trips`
                      : `${totalKmStr} km`
                  }`
                : ""}
              {parseFloat(variation) > 0
                ? ` (base ${baseKmStr} km + ${variation}%)`
                : ""}
            </div>
            {trips === 2 && adjKmStr && (
              <div className="cr-fare-saved-return">
                Includes return trip · {adjKmStr} km × 2 = {totalKmStr} km total
              </div>
            )}
          </div>
          <button
            className="cr-btn cr-btn--ghost cr-btn--xs"
            onClick={() => setSaved(false)}
          >
            Edit
          </button>
        </div>

      ) : (
        <>
          {/* ── Variation ── */}
          {kmReady && (
            <div className="cr-fare-variation-row">
              <span className="cr-fare-variation-label">Distance variation</span>
              <div className="cr-fare-variation-controls">
                <button
                  className="cr-fare-var-btn"
                  onClick={() => handleVariationChange((parseInt(variation) || 0) - 1)}
                  disabled={isSaving || parseInt(variation) <= 0}
                  aria-label="Decrease variation"
                >−</button>
                <div className="cr-fare-var-input-wrap">
                  <input
                    className="cr-input cr-fare-var-input"
                    type="number"
                    min="0"
                    max="99"
                    step="1"
                    value={variation}
                    onChange={e => handleVariationChange(e.target.value)}
                    disabled={isSaving}
                  />
                  <span className="cr-fare-var-pct">%</span>
                </div>
                <button
                  className="cr-fare-var-btn"
                  onClick={() => handleVariationChange((parseInt(variation) || 0) + 1)}
                  disabled={isSaving || parseInt(variation) >= 99}
                  aria-label="Increase variation"
                >+</button>
              </div>
              {parseFloat(variation) > 0 && (
                <div className="cr-fare-adj-km-tag">
                  {baseKmStr} + {variation}% = <strong>{adjKmStr} km</strong>
                </div>
              )}
            </div>
          )}

          {/* ── Trip type toggle ── */}
          {kmReady && (
            <div className="cr-fare-trips-row">
              <span className="cr-fare-variation-label">Trip type</span>
              <div className="cr-fare-trips-toggle">
                <button
                  className={`cr-fare-trip-btn ${trips === 1 ? "cr-fare-trip-btn--active" : ""}`}
                  onClick={() => handleTripsChange(1)}
                  disabled={isSaving}
                >
                  One way
                </button>
                <button
                  className={`cr-fare-trip-btn ${trips === 2 ? "cr-fare-trip-btn--active" : ""}`}
                  onClick={() => handleTripsChange(2)}
                  disabled={isSaving}
                >
                  Return
                </button>
              </div>
              {trips === 2 && adjKmStr && (
                <div className="cr-fare-adj-km-tag">
                  {adjKmStr} km × 2 = <strong>{totalKmStr} km</strong>
                </div>
              )}
            </div>
          )}

          {/* ── Rate × km = amount ── */}
          <div className="cr-fare-fields">
            <div className="cr-fare-field">
              <div className="cr-form-label">Rate per km</div>
              <div className="cr-fare-input-wrap">
                <span className="cr-fare-prefix">₹</span>
                <input
                  className="cr-input cr-fare-input"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={rate}
                  onChange={e => handleRateChange(e.target.value)}
                  disabled={isSaving || !kmReady}
                />
              </div>
              {kmReady && totalKmStr && (
                <div className="cr-fare-field-sub">× {totalKmStr} km</div>
              )}
            </div>

            <div className="cr-fare-op">=</div>

            <div className="cr-fare-field">
              <div className="cr-form-label">
                 Final amount {trips === 2 ? "(per trip)" : ""}
              </div>
              <div className="cr-fare-input-wrap">
                <span className="cr-fare-prefix">₹</span>
                <input
                  className="cr-input cr-fare-input"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={trips === 2 && amount ? String(Math.round(parseFloat(amount) / 2)) : amount}
                  onChange={e => handleAmountChange(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              {kmReady && rate && totalKmStr && (
                <div className="cr-fare-field-sub">
                 {trips === 2
                    ? `₹${parseFloat(rate).toFixed(2)}/km × ${adjKmStr} km × 2 = ₹${Number(amount).toLocaleString("en-IN")}`
                    : `= ₹${parseFloat(rate).toFixed(2)}/km × ${totalKmStr} km`}
                  </div>
              )}
            </div>
          </div>

          <div className="cr-action-row" style={{ marginTop: 12 }}>
            <button
              className="cr-btn cr-btn--primary"
              disabled={isSaving || !amount || parseFloat(amount) <= 0}
              onClick={handleSave}
            >
              {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
              {isSaving ? "Saving…" : "Save charges"}
            </button>
            {(rate || amount) && (
              <button
                className="cr-btn cr-btn--ghost"
                onClick={() => { setRate(""); setAmount(""); }}
                disabled={isSaving}
              >
                Clear
              </button>
            )}
          </div>

          {mutation.isError && (
            <p className="cr-error">{apiMsg(mutation.error)}</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Phase 1: Incident details ────────────────────────────────────────────────

function IncidentDetails({ raw, requestCode }) {
  return (
    <div className="qs-detail-grid">
      <DetailField label="Vehicle no."     value={raw.vehicleNumber}    />
      <DetailField label="Source"          value={raw.source === "whatsapp_bot" ? "WhatsApp bot" : "Admin"} />
      <DetailField label="Pickup"          value={raw.pickupLocation}   full />
      <DetailField label="Drop / dest."    value={raw.dropLocation}     full />
      {raw.issueDescription && (
        <DetailField label="Issue / condition" value={raw.issueDescription} full />
      )}
      <AccidentPhotosVerification requestCode={requestCode} raw={raw} />
    </div>
  );
}

// ─── Accident photo verification (upload / reupload / delete) ────────────────

const FIXED_PHOTO_SLOTS = [
  { key: "front_left",  label: "Front left"  },
  { key: "front_right", label: "Front right" },
  { key: "rear_left",   label: "Rear left"   },
  { key: "rear_right",  label: "Rear right"  },
];

function AccidentPhotosVerification({ requestCode, raw }) {
  console.log("accidentPhotos for", requestCode, raw.accidentPhotos);
  const photos = raw.accidentPhotos || {};
  const additional = Array.isArray(photos.additional) ? photos.additional : [];
  const filledCount = FIXED_PHOTO_SLOTS.filter(s => photos[s.key]).length;

  const uploadMutation = useUpdateQSPhotos(requestCode);
  const deleteMutation = useDeleteQSPhoto(requestCode);

  const [pendingKey, setPendingKey]       = useState(null); // slot key currently uploading, or "additional"
  const [confirmDelete, setConfirmDelete] = useState(null); // { slot, photoUrl } | null
  const inputRefs = useRef({});

  const handleFileChange = (slotKey) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingKey(slotKey);
    try {
      const fd = new FormData();
      fd.append(slotKey === "additional" ? "additional" : slotKey, file);
      await uploadMutation.mutateAsync(fd);
    } finally {
      setPendingKey(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    setPendingKey(confirmDelete.slot);
    try {
      await deleteMutation.mutateAsync(confirmDelete);
    } finally {
      setPendingKey(null);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="cr-photo-verify" style={{ gridColumn: "1 / -1" }}>
      <div className="cr-photo-verify-header">
        <span className="qs-detail-label">Accident photos</span>
        <span className={`cr-photo-verify-count ${filledCount === 4 ? "cr-photo-verify-count--ok" : "cr-photo-verify-count--warn"}`}>
          {filledCount}/4 fixed angles
        </span>
      </div>

      <div className="cr-photo-verify-grid">
        {FIXED_PHOTO_SLOTS.map(({ key, label }) => {
          const url  = photos[key];
          const busy = pendingKey === key;
          return (
            <div key={key} className="cr-photo-verify-slot">
              {url ? (
                <>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="cr-photo-verify-img-wrap">
                    <img src={url} alt={label} className="cr-photo-verify-img" />
                  </a>
                  <div className="cr-photo-verify-actions">
                    <button
                      className="cr-btn cr-btn--ghost cr-btn--xs"
                      disabled={busy}
                      onClick={() => inputRefs.current[key]?.click()}
                    >
                      {busy ? <span className="qs-btn-spinner" style={{ width: 9, height: 9 }} /> : <ReuploadIcon />}
                      Reupload
                    </button>
                    <button
                      className="cr-btn cr-btn--ghost cr-btn--xs cr-btn--danger"
                      disabled={busy}
                      onClick={() => setConfirmDelete({ slot: key, photoUrl: url })}
                    >
                      <XSmIcon /> Delete
                    </button>
                  </div>
                </>
              ) : (
                <button
                  className="cr-photo-verify-empty"
                  disabled={busy}
                  onClick={() => inputRefs.current[key]?.click()}
                >
                  {busy ? <span className="qs-btn-spinner" /> : <CameraSmIcon />}
                  <span>{busy ? "Uploading…" : "Missing"}</span>
                </button>
              )}
              <div className="cr-photo-verify-label">{label}</div>
              <input
                ref={el => (inputRefs.current[key] = el)}
                type="file" accept="image/*" style={{ display: "none" }}
                onChange={handleFileChange(key)}
              />
            </div>
          );
        })}
      </div>

      <div className="cr-photo-verify-additional">
        <div className="cr-additional-label">
          Additional photos
          {additional.length > 0 && <span style={{ marginLeft: 6, opacity: 0.6 }}>({additional.length})</span>}
        </div>
        <div className="cr-additional-preview-row">
          {additional.map((url, idx) => (
            <div key={idx} className="cr-additional-thumb-wrap">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Extra ${idx + 1}`} className="cr-photo-thumb" />
              </a>
              <button
                className="cr-photo-remove-btn"
                onClick={() => setConfirmDelete({ slot: "additional", photoUrl: url })}
              >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            </div>
          ))}
          <button
            className="cr-add-photo-btn"
            disabled={pendingKey === "additional"}
            onClick={() => inputRefs.current.additional?.click()}
          >
            {pendingKey === "additional" ? <span className="qs-btn-spinner" /> : <PlusIcon />}
            Add photo
          </button>
          <input
            ref={el => (inputRefs.current.additional = el)}
            type="file" accept="image/*" style={{ display: "none" }}
            onChange={handleFileChange("additional")}
          />
        </div>
      </div>

      {uploadMutation.isError && (
        <p className="cr-error" style={{ marginTop: 6 }}>
          {apiMsg(uploadMutation.error, "Failed to upload photo. Please try again.")}
        </p>
      )}
      {deleteMutation.isError && (
        <p className="cr-error" style={{ marginTop: 6 }}>
          {apiMsg(deleteMutation.error, "Failed to delete photo. Please try again.")}
        </p>
      )}

      {confirmDelete && (
        <div className="cr-confirm-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="cr-confirm-box">
            <p>Delete this photo? You can reupload afterward if needed.</p>
            <div className="cr-action-row">
              <button className="cr-btn cr-btn--danger" disabled={deleteMutation.isPending} onClick={handleDeleteConfirmed}>
                {deleteMutation.isPending ? <span className="qs-btn-spinner" /> : <XSmIcon />}
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
              <button className="cr-btn cr-btn--ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Phase 2: Owner & driver info ────────────────────────────────────────────

function Phase2OwnerDriver({ requestCode, raw, status }) {
  const phaseState = hasReached(status, "VEHICLE_SELECTED") ? "done"
                   : hasReached(status, "NEW")              ? "active"
                   : "locked";

  const filled = !!(raw.customerName || raw.driverName || raw.driverContact);
  const badge = filled
  ? raw.customerName || raw.driverName || "Saved"
  : status === "NEW" ? "Action needed" : "Pending";

  const mutation = useUpdateQSDetails(requestCode);

  const [customerName,     setCustomerName]     = useState(raw.customerName     || "");
  const [driverName,    setDriverName]    = useState(raw.driverName    || "");
  const [driverContact, setDriverContact] = useState(raw.driverContact || "");
  const [editing,       setEditing]       = useState(!filled);

  // Sync when raw data arrives asynchronously
  useEffect(() => {
    if (filled) {
      setEditing(false);
      // Also sync input state in case raw arrived after initial render
      setCustomerName(raw.customerName     || "");
      setDriverName(raw.driverName    || "");
      setDriverContact(raw.driverContact || "");
    }
  }, [filled]);

  const canEdit   = !hasReached(status, "ASSIGNED");
  const isSaving  = mutation.isPending;

  const handleSave = async () => {
    await mutation.mutateAsync({ customerName, driverName, driverContact });
    setEditing(false);
  };

  return (
    <CRPhase
      number={2}
      title="Owner &amp; driver details"
      state={phaseState}
      defaultOpen={status === "NEW" || status === "DETAILS_PENDING"}
      badge={badge}
    >
      {filled && !editing ? (
        <>
          <div className="qs-detail-grid">
            <DetailField label="Customer name"      value={raw.customerName     || "—"} />
            <DetailField label="Driver name"     value={raw.driverName    || "—"} />
            <DetailField label="Driver contact"  value={raw.driverContact || "—"} />
          </div>
          {canEdit && (
            <div className="cr-action-row">
              {/* Show confirm button if details not yet saved to backend */}
                {status === "NEW" && (
                  <button
                    className="cr-btn cr-btn--primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
                    {isSaving ? "Saving…" : "Confirm details"}
                  </button>
                )}
              <button
                className="cr-btn cr-btn--ghost"
                onClick={() => {
                  // Re-sync inputs with latest raw before opening form
                  setCustomerName(raw.customerName     || "");
                  setDriverName(raw.driverName    || "");
                  setDriverContact(raw.driverContact || "");
                  setEditing(true);
                }}
              >
                Edit
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="cr-form-grid">
            <FormField label="Customer name" placeholder="e.g. Rajesh Sharma">
              <input
                className="cr-input"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                disabled={isSaving}
              />
            </FormField>
            <FormField label="Driver / representative name" placeholder="Who will receive the vehicle">
              <input
                className="cr-input"
                value={driverName}
                onChange={e => setDriverName(e.target.value)}
                disabled={isSaving}
              />
            </FormField>
            <FormField label="Driver contact" placeholder="10-digit mobile">
              <input
                className="cr-input"
                type="tel"
                value={driverContact}
                 onChange={e => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setDriverContact(digits);
                    }}
                  disabled={isSaving}
                  placeholder="10-digit mobile"
                  maxLength={10}
                  inputMode="numeric"
              />
            </FormField>
          </div>
          <div className="cr-action-row">
            <button
              className="cr-btn cr-btn--primary"
              onClick={handleSave}
              disabled={isSaving || (!customerName && !driverName && !driverContact)}
            >
              {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
              {isSaving ? "Saving…" : "Save details"}
            </button>
            {filled && (
             <button
                className="cr-btn cr-btn--ghost"
                onClick={() => {
                  setCustomerName(raw.customerName     || "");
                  setDriverName(raw.driverName    || "");
                  setDriverContact(raw.driverContact || "");
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            )}
          </div>
          {mutation.isError && (
            <p className="cr-error">{apiMsg(mutation.error)}</p>
          )}
        </>
      )}
    </CRPhase>
  );
}

// ─── Phase 3: Support vehicle selection ───────────────────────────────────────

function Phase3VehicleSelection({ requestCode, raw, status }) {
  const unlocked   = hasReached(status, "DETAILS_PENDING");
  const phaseState = hasReached(status, "ASSIGNED") ? "done"
                   : unlocked                       ? "active"
                   : "locked";

  const badge = raw.supportVehicleType
    ? `${VEHICLE_TYPE_OPTIONS.find(o => o.value === raw.supportVehicleType)?.label || raw.supportVehicleType}${raw.supportVehicleSize ? ` · ${raw.supportVehicleSize}` : ""}`
    : unlocked ? "Select vehicle" : "Waiting";

  const mutation = useUpdateQSVehicleSelection(requestCode);

  const [vType, setVType] = useState(raw.supportVehicleType || "");
  const [vSize, setVSize] = useState(raw.supportVehicleSize || "");
  const [editing, setEditing] = useState(!raw.supportVehicleType);

  const canEdit  = !hasReached(status, "ASSIGNED");
  const isSaving = mutation.isPending;

  const handleSave = async () => {
    await mutation.mutateAsync({ supportVehicleType: vType, supportVehicleSize: vSize });
    setEditing(false);
  };

  return (
    <CRPhase
      number={3}
      title="Support vehicle selection"
      state={phaseState}
      defaultOpen={status === "DETAILS_PENDING"}
      badge={badge}
    >
      {!unlocked && (
        <p className="cr-locked-note">Complete owner/driver details first.</p>
      )}

      {unlocked && raw.supportVehicleType && !editing ? (
        <>
          <div className="qs-detail-grid">
            <DetailField
              label="Vehicle type"
              value={VEHICLE_TYPE_OPTIONS.find(o => o.value === raw.supportVehicleType)?.label || raw.supportVehicleType}
            />
            <DetailField
              label="Size"
              value={raw.supportVehicleSize
                ? raw.supportVehicleSize.charAt(0).toUpperCase() + raw.supportVehicleSize.slice(1)
                : "—"}
            />
          </div>
          {canEdit && (
            <div className="cr-action-row">
              <button className="cr-btn cr-btn--ghost" onClick={() => setEditing(true)}>
                Change selection
              </button>
            </div>
          )}
        </>
      ) : unlocked ? (
        <>
          {/* Vehicle type buttons */}
          <div className="cr-form-section-label">Vehicle type</div>
          <div className="cr-option-group">
            {VEHICLE_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`cr-option-btn ${vType === opt.value ? "cr-option-btn--active" : ""}`}
                onClick={() => setVType(opt.value)}
                disabled={isSaving}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Size buttons */}
          <div className="cr-form-section-label" style={{ marginTop: 12 }}>Size</div>
          <div className="cr-option-group">
            {VEHICLE_SIZE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`cr-option-btn ${vSize === opt.value ? "cr-option-btn--active" : ""}`}
                onClick={() => setVSize(opt.value)}
                disabled={isSaving}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="cr-action-row" style={{ marginTop: 14 }}>
            <button
              className="cr-btn cr-btn--primary"
              onClick={handleSave}
              disabled={isSaving || !vType || !vSize}
            >
              {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
              {isSaving ? "Saving…" : "Confirm selection"}
            </button>
            {raw.supportVehicleType && (
              <button className="cr-btn cr-btn--ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
          </div>
          {mutation.isError && (
            <p className="cr-error">{apiMsg(mutation.error)}</p>
          )}
        </>
      ) : null}
    </CRPhase>
  );
}

// ─── Phase 4: Vendor + vehicle assignment ─────────────────────────────────────

function Phase4Assignment({ requestCode, raw, status, onNavigateVendors }) {
  const unlocked   = hasReached(status, "VEHICLE_SELECTED");
  const phaseState = hasReached(status, "ASSIGNED") ? "done"
                   : unlocked                       ? "active"
                   : "locked";

  const badge = raw.vendorName
    ? raw.vendorName
    : unlocked ? "Assign vendor" : "Waiting";

  const canReassign      = !hasReached(status, "EN_ROUTE");
  const vehicleAssigned  = !!(raw.vrRegistrationNumber || raw.dispatchedDriverName);
  const [vehicleEditing, setVehicleEditing] = useState(!vehicleAssigned);

  return (
    <CRPhase
      number={4}
      title="Vendor &amp; vehicle assignment"
      state={phaseState}
      defaultOpen={status === "VEHICLE_SELECTED" || status === "ASSIGNED"}
      badge={badge}
    >
      {!unlocked && (
        <p className="cr-locked-note">Select support vehicle type first.</p>
      )}

      {unlocked && (
        <>
          {/* ── Step 1: Vendor ── */}
          <div className="cr-sub-section-label">
            <span className={`cr-sub-step-dot ${raw.vendorId ? "cr-sub-step-dot--done" : "cr-sub-step-dot--active"}`}>
              {raw.vendorId ? <CheckSmIcon /> : "1"}
            </span>
            Vendor
          </div>

          {raw.vendorId ? (
            <div className="cr-vendor-assigned-row">
              <div className="cr-vendor-avatar">
                {(raw.vendorName || "V").slice(0, 2).toUpperCase()}
              </div>
              <div className="cr-vendor-assigned-info">
                <div className="cr-vendor-assigned-name">{raw.vendorName}</div>
                {raw.vendorPhone && (
                  <div className="cr-vendor-assigned-phone">{raw.vendorPhone}</div>
                )}
              </div>
              {canReassign && (
                <button className="cr-btn cr-btn--ghost cr-btn--xs" onClick={onNavigateVendors}>
                  Change
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="cr-info-note">
                Choose a vendor from the vendor list. The request code will be pre-filled on the vendor page.
              </p>
              <div className="cr-action-row">
                <button className="cr-btn cr-btn--primary" onClick={onNavigateVendors}>
                  <VendorIcon /> Go to vendor page
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: Vehicle & driver — only after vendor assigned ── */}
          {raw.vendorId && (
            <>
              <div className="cr-sub-section-divider" />
              <div className="cr-sub-section-label">
                <span className={`cr-sub-step-dot ${vehicleAssigned ? "cr-sub-step-dot--done" : "cr-sub-step-dot--active"}`}>
                  {vehicleAssigned ? <CheckSmIcon /> : "2"}
                </span>
                Vehicle &amp; driver
              </div>

              {vehicleAssigned && !vehicleEditing ? (
                /* ── Read-only vehicle view with driver edit option ── */
                <VehicleReadOnly
                  raw={raw}
                  requestCode={requestCode}
                  canEdit={canReassign}
                  onChangeVehicle={() => setVehicleEditing(true)}
                />
              ) : (
                /* ── Vehicle picker + driver fields ── */
                <VehiclePickerSection
                  requestCode={requestCode}
                  vendorId={raw.vendorId}
                  onAssigned={() => setVehicleEditing(false)}
                />
              )}
            </>
          )}
        </>
      )}
    </CRPhase>
  );
}

// ─── Phase 4.5: Payment confirmation ─────────────────────────────────────────

function Phase4bPayment({ requestCode, raw, status }) {
  const unlocked        = hasReached(status, "ASSIGNED");
  const paymentConfirmed = !!raw.customerAcceptedVendor;

  const phaseState = paymentConfirmed ? "done"
                   : unlocked         ? "active"
                   : "locked";

  const badge = paymentConfirmed
    ? raw.paymentMode
      ? `Confirmed · ${raw.paymentMode}`
      : "Confirmed"
    : unlocked
    ? "Action needed"
    : "Waiting";

  const mutation = useAdminConfirmPayment(requestCode);
  const isSaving = mutation.isPending;
  const [payLater, setPayLater] = useState(false);

  // ── Customer charge (separate from vendor charges in raw.charges) ─────────
  const vendorCharges = raw.charges ? Number(raw.charges) : null;
  const [customerCharge, setCustomerCharge] = useState(
    raw.customerCharge != null ? String(raw.customerCharge)
      : vendorCharges != null ? String(vendorCharges)
      : ""
  );
  const [lowChargeReason, setLowChargeReason] = useState(raw.lowChargeReason || "");
  // const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  const [selectedMode, setSelectedMode] = useState("CASH"); // default to first option

  const customerChargeNum = parseFloat(customerCharge);
  const isBelowVendorCharge = vendorCharges != null && !isNaN(customerChargeNum) && customerChargeNum < vendorCharges;

  const handleCustomerChargeChange = (val) => {
    setCustomerCharge(val);
    const num = parseFloat(val);
    const nowBelow = vendorCharges != null && !isNaN(num) && num < vendorCharges;
      if (!nowBelow) {
        setLowChargeReason(""); // clear stale reason if amount is no longer below vendor charge
      }
  };

  const canConfirm = !!customerCharge && customerChargeNum > 0 && (!isBelowVendorCharge || lowChargeReason.trim().length > 0);

  const handleConfirm = () => {
    mutation.mutate({
      paymentMode: payLater ? "PAY_LATER" : selectedMode,
      customerCharge: customerChargeNum,
      ...(isBelowVendorCharge ? { lowChargeReason: lowChargeReason.trim() } : {}),
    });
  };

  return (
    <CRPhase
      number="4b"
      title="Payment confirmation"
      state={phaseState}
      defaultOpen={unlocked && !paymentConfirmed}
      badge={badge}
    >
      {!unlocked && (
        <p className="cr-locked-note">Assign vendor and vehicle first.</p>
      )}

      {/* Fare calculator — always shown once unlocked */}
      {unlocked && (
        <FareCalculator requestCode={requestCode} raw={raw} />
      )}

      {unlocked && <div className="cr-sub-section-divider" />}

      {/* Confirmed state */}
      {unlocked && paymentConfirmed && (
        <div className="cr-payment-confirmed-banner">
          <CheckSmIcon />
          <div>
            <div className="cr-payment-confirmed-title">Payment confirmed</div>
            {raw.paymentMode && (
              <div className="cr-payment-confirmed-sub">
                Mode: {PAYMENT_MODE_LABEL[raw.paymentMode] || raw.paymentMode}
              </div>
            )}
             {raw.customerCharge != null && (
              <div className="cr-payment-confirmed-sub">
                Charged to customer: ₹{Number(raw.customerCharge).toLocaleString("en-IN")}
              </div>
            )}
            {vendorCharges != null && (
              <div className="cr-payment-confirmed-sub">
                Vendor charges: ₹{vendorCharges.toLocaleString("en-IN")}
              </div>
            )}
             {customerCharge && (
              <div className="cr-payment-confirmed-sub">
                Customer Charges: {customerCharge}
              </div>
            )}
            {raw.lowChargeReason && (
              <div className="cr-payment-confirmed-sub">
                Reason for lower charge: {raw.lowChargeReason}
              </div>
            )}
            <div className="cr-payment-confirmed-sub">
              Vendor can now allow driver to proceed.
            </div>
          </div>
        </div>
      )}

      {/* Pending payment */}
      {unlocked && !paymentConfirmed && (
        <>
          <p className="cr-info-note">
            Confirm payment so the vendor can allow the driver to proceed.
            Set the amount to charge the customer and choose the payment arrangement below.
          </p>

          {/* Vendor charges reference (read-only, from fare calculator) */}
          {vendorCharges != null && (
            <div className="cr-payment-amount-row">
              <span className="cr-payment-amount-label">Vendor charges (agreed)</span>
              <span className="cr-payment-amount-value">
                ₹{vendorCharges.toLocaleString("en-IN")}
              </span>
            </div>
          )}

           {/* Customer charge — editable, independent of vendor charges */}
          <div className="cr-fare-field" style={{ marginTop: 10, marginBottom: 4 }}>
            <div className="cr-form-label">Charges to collect from customer</div>
            <div className="cr-fare-input-wrap">
              <span className="cr-fare-prefix">₹</span>
              <input
                className="cr-input cr-fare-input"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={customerCharge}
                onChange={e => handleCustomerChargeChange(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Alert + mandatory reason if below vendor charge */}
          {isBelowVendorCharge && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "12px 14px",
                marginTop: 8,
                marginBottom: 14,
              }}
            >
              <p style={{ margin: "0 0 8px", fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
                ⚠️ Amount is less than vendor charges (₹{vendorCharges.toLocaleString("en-IN")})
              </p>
              <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "#7f1d1d" }}>
                Please provide a reason for charging the customer less than the vendor's agreed amount.
              </p>
              <textarea
                className="cr-input"
                style={{ width: "100%", minHeight: 60, resize: "vertical" }}
                placeholder="e.g. Customer is a repeat client, discount approved by manager, etc."
                value={lowChargeReason}
                onChange={e => setLowChargeReason(e.target.value)}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Payment options */}
          <div className="cr-payment-options">

            {/* Option A: Payment received */}
            <div
              className={`cr-payment-option ${!payLater ? "cr-payment-option--selected" : ""}`}
              onClick={() => setPayLater(false)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && setPayLater(false)}
            >
              <div className="cr-payment-option-dot">
                {!payLater ? <CheckSmIcon /> : ""}
              </div>
              <div className="cr-payment-option-body">
                <div className="cr-payment-option-title">
                  <CashIcon /> Payment received
                </div>
                <div className="cr-payment-option-sub">
                  Customer has already paid. Driver can proceed immediately.
                </div>
                {!payLater && (
                  <div className="cr-payment-mode-row">
                    {["CASH", "ONLINE", "UPI", "CHEQUE"].map(mode => (
                      <PaymentModeChip
                        key={mode}
                        mode={mode}
                        selected={selectedMode === mode}
                        onSelect={() => setSelectedMode(mode)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Option B: Pay later */}
            <div
              className={`cr-payment-option ${payLater ? "cr-payment-option--selected" : ""}`}
              onClick={() => setPayLater(true)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && setPayLater(true)}
            >
              <div className="cr-payment-option-dot">
                {payLater ? <CheckSmIcon /> : ""}
              </div>
              <div className="cr-payment-option-body">
                <div className="cr-payment-option-title">
                  <ClockPayIcon /> Customer will pay later
                </div>
                <div className="cr-payment-option-sub">
                  Dispatch driver now. Payment will be collected on delivery or after service.
                </div>
              </div>
            </div>
          </div>

          <div className="cr-action-row" style={{ marginTop: 16 }}>
            <button
              className="cr-btn cr-btn--primary"
              disabled={isSaving || !canConfirm}
              onClick={handleConfirm}
              
            >
              {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
              {isSaving
                ? "Confirming…"
                : payLater
                ? "Confirm — driver can proceed"
                : "Confirm payment received"}
            </button>
          </div>

          {isBelowVendorCharge && !lowChargeReason.trim() && (
            <p className="cr-error" style={{ marginTop: 8 }}>
              Please enter a reason before confirming a lower charge.
            </p>
          )}

          {mutation.isError && (
            <p className="cr-error">{apiMsg(mutation.error)}</p>
          )}
        </>
      )}
    </CRPhase>
  );
}

// Small mode chip — purely visual, admin just picks one optionally
function PaymentModeChip({ mode, selected, onSelect }) {
  return (
    <button
      className={`cr-mode-chip ${selected ? "cr-mode-chip--active" : ""}`}
      onClick={e => { e.stopPropagation(); onSelect(); }}
      type="button"
    >
      {PAYMENT_MODE_LABEL[mode] || mode}
    </button>
  );
}

// ─── Vehicle read-only view with inline driver edit ───────────────────────────

function VehicleReadOnly({ raw, requestCode, canEdit, onChangeVehicle }) {
  console.log("Vehicle read-only data", raw);
   const navigate = useNavigate(); 
  const [editingDriver, setEditingDriver] = useState(false);
  const [driverName,    setDriverName]    = useState(raw.dispatchedDriverName    || "");
  const [driverContact, setDriverContact] = useState(raw.dispatchedDriverContact || "");

  const mutation = useAssignQSVehicle(requestCode);
  const isSaving = mutation.isPending;
  const vehicleNumber = raw.vehicleNumber;

  const handleSaveDriver = async () => {
    // Only update driver fields — keep existing vehicle
    await mutation.mutateAsync({
      registrationNumber:      raw.vehicleNumber || "",
      dispatchedDriverName:    driverName,
      dispatchedDriverContact: driverContact,
    });
    setEditingDriver(false);
  };

  return (
    <>
      <div className="qs-detail-grid">
        <DetailField label="Vehicle no."
          value={raw.vehicleNumber || "—"} />
        {raw.vrMaker && (
          <DetailField label="Make / model"
            value={[raw.vrMaker, raw.vrModel].filter(Boolean).join(" ")} />
        )}
      </div>
      {vehicleNumber && (
        <button
          className="cr-fastag-btn"
          onClick={() => navigate(`/fastag-details/${encodeURIComponent(vehicleNumber)}`)}
        >
          <FastagIcon />
          Track on FASTag dashboard
          <span className="cr-fastag-btn-arrow">↗</span>
        </button>
      )}

      <div className="cr-driver-block">
        <div className="cr-driver-block-header">
          <div className="cr-driver-block-title">
            <DriverIcon /> Dispatched driver
          </div>
          {canEdit && !editingDriver && (
            <button
              className="cr-btn cr-btn--ghost cr-btn--xs"
              onClick={() => setEditingDriver(true)}
            >
              Edit driver
            </button>
          )}
        </div>

        {editingDriver ? (
          /* ── Inline driver edit ── */
          <>
            <div className="cr-form-grid" style={{ marginTop: 8 }}>
              <FormField label="Driver name">
                <input
                  className="cr-input"
                  value={driverName}
                  onChange={e => setDriverName(e.target.value)}
                  disabled={isSaving}
                  placeholder="e.g. Suresh Kumar"
                />
              </FormField>
              <FormField label="Driver contact">
                <input
                  className="cr-input"
                  type="tel"
                  value={driverContact}
                  onChange={e => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setDriverContact(digits);
                    }}
                  disabled={isSaving}
                  placeholder="10-digit mobile"
                  maxLength={10}
                  inputMode="numeric"
                />
              </FormField>
            </div>
            <div className="cr-action-row">
              <button
                className="cr-btn cr-btn--primary"
                disabled={isSaving}
                onClick={handleSaveDriver}
              >
                {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
                {isSaving ? "Saving…" : "Update driver"}
              </button>
              <button
                className="cr-btn cr-btn--ghost"
                onClick={() => {
                  setDriverName(raw.dispatchedDriverName    || "");
                  setDriverContact(raw.dispatchedDriverContact || "");
                  setEditingDriver(false);
                }}
              >
                Cancel
              </button>
            </div>
            {mutation.isError && (
              <p className="cr-error">{apiMsg(mutation.error) || "Failed to update driver. Please try again."}</p>
            )}
          </>
        ) : (
          /* ── Driver read-only ── */
          <div className="cr-driver-row">
            <div className="cr-driver-name">
              {raw.dispatchedDriverName || <span style={{ color: "var(--wd-t3)", fontStyle: "italic" }}>Not set</span>}
            </div>
            {raw.dispatchedDriverContact && (
              <div className="cr-driver-contact">{raw.dispatchedDriverContact}</div>
            )}
          </div>
        )}
      </div>

      {/* Change vehicle button — separate from edit driver */}
      {canEdit && (
        <div className="cr-action-row" style={{ marginTop: 4 }}>
          <button className="cr-btn cr-btn--ghost" onClick={onChangeVehicle}>
            Change vehicle
          </button>
        </div>
      )}
    </>
  );
}

// ─── Vehicle picker section ───────────────────────────────────────────────────

function VehiclePickerSection({ requestCode, vendorId, onAssigned }) {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [driverName,      setDriverName]       = useState("");
  const [driverContact,   setDriverContact]    = useState("");

  // ── Use the hook instead of a manual fetch ────────────────────────────────
  const {
    data:      vehicleData,
    isLoading: loadingList,
    isError:   listFailed,
  } = useVendorVehicles(vendorId);
  const fetchAndSave = useFetchAndSaveVehicle(requestCode);

 console.log("raw vehicle row", vehicleData?.data?.[0]); // see actual field names

  const vehicles = vehicleData?.data || [];

  const mutation = useAssignQSVehicle(requestCode);
  const isSaving = mutation.isPending;

  const handleSelect = (v) => {
    setSelectedVehicle(v);
    // Pre-fill driver name/contact from the vehicle row, admin can override
    setDriverName(v.driverName || v.ownerName || "");
    setDriverContact(v.driverNumber || "");
  };

  const handleConfirm = async () => {
    console.log("selectedVehicle", selectedVehicle);
    console.log("plate being sent", selectedVehicle?.vehicleNo);
    const vehicleNo = selectedVehicle?.vehicleNo;
  if (vehicleNo) {
    fetchAndSave.mutate(vehicleNo);
  }
    await mutation.mutateAsync({
      registrationNumber:      vehicleNo,
      dispatchedDriverName:    driverName,
      dispatchedDriverContact: driverContact,
    });
    onAssigned();
  };

  // ── States ─────────────────────────────────────────────────────────────────
  if (loadingList) {
    return (
      <div className="cr-vehicle-loading">
        <span className="cr-plate-spinner" /> Loading vehicles…
      </div>
    );
  }

  if (listFailed) {
    return <p className="cr-error">Failed to load vehicles for this vendor.</p>;
  }

  return (
    <div className="cr-vehicle-picker">
      {vehicles.length === 0 ? (
        <p className="cr-info-note">No active vehicles found for this vendor.</p>
      ) : (
        <div className="cr-vehicle-list">
          {vehicles.map(v => {
            const isSelected = selectedVehicle?.id === v.id;
            const plate      = v.vehicleNo || v.Vehicle_rc_regn_no || "—";
            const type       = v.craneType || v.type || "—";
            const driver     = v.driverName || v.ownerName || "—";
            const isBusy     = v.isWorking === 1;

            return (
              <div
                key={v.id}
                className={`cr-vehicle-row ${isSelected ? "cr-vehicle-row--selected" : ""} ${isBusy ? "cr-vehicle-row--busy" : ""}`}
                onClick={() => !isBusy && handleSelect(v)}
                title={isBusy ? "Vehicle is currently on another job" : undefined}
              >
                <div className="cr-vehicle-icon"><TruckSmIcon /></div>
                <div className="cr-vehicle-info">
                  <div className="cr-vehicle-plate">{plate}</div>
                  <div className="cr-vehicle-meta">
                    {type}
                    {v.craneCapacity ? ` · ${v.craneCapacity}T` : ""}
                    {" · "}
                    {driver}
                    {isBusy && (
                      <span className="cr-vehicle-busy-tag"> · On job</span>
                    )}
                  </div>
                </div>
                <div className={`cr-vehicle-select-dot ${isSelected ? "cr-vehicle-select-dot--active" : ""}`} />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Driver override fields — only shown after a vehicle is selected ── */}
      {selectedVehicle && (
        <>
          <div className="cr-sub-section-label" style={{ marginTop: 12 }}>
            <DriverIcon /> Dispatched driver
            <span style={{ fontWeight: 400, opacity: 0.6, textTransform: "none", fontSize: 11 }}>
              &nbsp;— overridable
            </span>
          </div>
          <div className="cr-form-grid">
            <FormField label="Driver name">
              <input
                className="cr-input"
                value={driverName}
                onChange={e => setDriverName(e.target.value)}
                disabled={isSaving}
                placeholder="e.g. Suresh Kumar"
              />
            </FormField>
            <FormField label="Driver contact">
              <input
                className="cr-input"
                type="tel"
                value={driverContact}
                onChange={e => setDriverContact(e.target.value)}
                disabled={isSaving}
                placeholder="10-digit mobile"
              />
            </FormField>
          </div>

          <div className="cr-action-row">
            <button
              className="cr-btn cr-btn--primary"
              disabled={isSaving}
              onClick={handleConfirm}
            >
              {isSaving ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
              {isSaving ? "Saving…" : "Confirm vehicle"}
            </button>
          </div>

          {mutation.isError && (
            <p className="cr-error">Failed to save vehicle. Please try again.</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Phase 5: Dispatch & live track ──────────────────────────────────────────

function Phase5Dispatch({ requestCode, raw, status, onTrack }) {
  const unlocked = hasReached(status, "ASSIGNED");
 
  const phaseState = status === "DONE"      ? "done"
                   : status === "CANCELLED" ? "done"
                   : unlocked               ? "active"
                   : "locked";
 
  const badge = {
    DONE:               "Completed",
    WORKING:            "Working",
    OTP_VERIFIED:       "OTP verified",
    VEHICLE_INSPECTED:  "Vehicle inspected",
    REACHED_AT_PICKUP:  "At pickup",
    MOVED:              "En route",
    ASSIGNED:           "Ready to dispatch",
  }[status] || "Waiting";
 
  // ── Mutations ─────────────────────────────────────────────────────────────
  const statusMutation       = useUpdateQSStatus(requestCode);
  const confirmReachedMut    = useAdminConfirmReached(requestCode);
  const confirmInspectionMut = useAdminConfirmInspection(requestCode);
  const confirmOTPMut        = useAdminConfirmOTP(requestCode);
  const confirmDoneMut       = useAdminConfirmDone(requestCode);
 
  const [otpInput,      setOtpInput]      = useState("");
  const [generatedOtp,  setGeneratedOtp]  = useState(null);
 
  const displayOtp = generatedOtp || raw.otp || null;
 
  // ── Step definitions ──────────────────────────────────────────────────────
  const STEPS = [
    { key: "ASSIGNED",          label: "Assigned",            sub: "Vendor & driver confirmed"     },
    { key: "MOVED",             label: "Driver en route",     sub: "Driver moving to pickup"        },
    { key: "REACHED_AT_PICKUP", label: "Reached pickup",      sub: "Driver at pickup location"      },
    { key: "VEHICLE_INSPECTED", label: "Vehicle inspected",   sub: "Inspection confirmed"           },
    { key: "OTP_VERIFIED",      label: "OTP verified",        sub: "Customer handed over vehicle"   },
    { key: "WORKING",           label: "Working / en route",  sub: "Moving to drop location"        },
    { key: "DONE",              label: "Done",                sub: "Job complete"                   },
  ];
 
  // For stepper: map OTP_VERIFIED / VEHICLE_INSPECTED onto their position
  const STATUS_STEP_ORDER = STEPS.map(s => s.key);
  const currentIdx = STATUS_STEP_ORDER.indexOf(status);
 
  return (
    <CRPhase
      number={5}
      title="Dispatch &amp; tracking"
      state={phaseState}
      defaultOpen={unlocked && status !== "DONE"}
      badge={badge}
    >
      {!unlocked && (
        <p className="cr-locked-note">Assign vendor and vehicle first.</p>
      )}
 
      {unlocked && (
        <>
          {/* ── Progress stepper ── */}
          <div className="cr-dispatch-stepper">
            {STEPS.map((step, idx) => {
              const isDone   = idx < currentIdx;
              const isActive = idx === currentIdx;
              return (
                <div key={step.key} className="cr-dispatch-step-wrap">
                  <div className="cr-dispatch-step">
                    <div className={[
                      "cr-dispatch-dot",
                      isDone   ? "cr-dispatch-dot--done"   : "",
                      isActive ? "cr-dispatch-dot--active" : "",
                    ].filter(Boolean).join(" ")}>
                      {isDone ? <CheckSmIcon /> : idx + 1}
                    </div>
                    <div className="cr-dispatch-step-info">
                      <div className={[
                        "cr-dispatch-step-title",
                        isActive ? "cr-dispatch-step-title--active" : "",
                      ].filter(Boolean).join(" ")}>
                        {step.label}
                      </div>
                      <div className="cr-dispatch-step-sub">{step.sub}</div>
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`cr-dispatch-line ${isDone ? "cr-dispatch-line--done" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>
 
          {/* ── Action area ── */}
          <div style={{ marginTop: 16 }}>
 
            {/* ── ASSIGNED → MOVED ── */}
            {status === "ASSIGNED" && (
              <div className="cr-action-row">
                <button
                  className="cr-btn cr-btn--primary"
                  disabled={statusMutation.isPending}
                  onClick={() => statusMutation.mutate("MOVED")}
                >
                  {statusMutation.isPending ? <span className="qs-btn-spinner" /> : <TruckSmIcon />}
                  {statusMutation.isPending ? "Updating…" : "Mark driver en route"}
                </button>
                {statusMutation.isError && (
                  <p className="cr-error">{apiMsg(statusMutation.error) || "Failed to update. Try again."}</p>
                )}
              </div>
            )}
 
            {/* ── MOVED → REACHED_AT_PICKUP ── */}
            {status === "MOVED" && (
              <div className="cr-action-row">
                {raw.approvedReaching ? (
      // Customer already confirmed — show info, no button needed
      <div className="cr-payment-confirmed-banner">
        <CheckSmIcon />
        <div>
          <div className="cr-payment-confirmed-title">
            Reached confirmed by customer
          </div>
          <div className="cr-payment-confirmed-sub">
            Syncing status to inspection phase…
          </div>
        </div>
      </div>
    ): (
                <button
                  className="cr-btn cr-btn--primary"
                  disabled={confirmReachedMut.isPending}
                  onClick={() => confirmReachedMut.mutate()}
                >
                  {confirmReachedMut.isPending ? <span className="qs-btn-spinner" /> : <CheckSmIcon />}
                  {confirmReachedMut.isPending ? "Confirming…" : "Confirm driver reached pickup"}
                </button>
    )}
                <button className="cr-btn cr-btn--secondary" onClick={onTrack}>
                  <MapPinSmIcon /> Live track
                </button>
                {confirmReachedMut.isError && (
                  <p className="cr-error">
                    {confirmReachedMut.error?.response?.data?.message || "Failed. Try again."}
                  </p>
                )}
              </div>
            )}
 
            {/* ── REACHED_AT_PICKUP: inspection + OTP sub-steps ── */}
            {(status === "REACHED_AT_PICKUP" || status === "VEHICLE_INSPECTED") && (
              <div className="cr-sub-steps">
 
                {/* Sub-step A: Vehicle inspection */}
                <SubStep
                  number="A"
                  title="Confirm vehicle inspection"
                  sub="Driver has inspected the vehicle condition"
                  done={!!raw.approvedRightlyMeasured || status === "VEHICLE_INSPECTED"}
                  doneLabel={
                    raw.timeDiffRToC
                      ? `Confirmed · Waited ${raw.timeDiffRToC}`
                      : "Inspection confirmed"
                  }
                  doneBy={raw.approvedRightlyMeasured ? "Confirmed" : null}
                >
                  <div className="cr-action-row">
                    <button
                      className="cr-btn cr-btn--primary"
                      disabled={confirmInspectionMut.isPending}
                      onClick={() =>
                        confirmInspectionMut.mutateAsync().then(res => {
                          if (res?.data?.otp) setGeneratedOtp(res.data.otp);
                        })
                      }
                    >
                      {confirmInspectionMut.isPending
                        ? <span className="qs-btn-spinner" />
                        : <CheckSmIcon />}
                      {confirmInspectionMut.isPending
                        ? "Confirming…"
                        : "Confirm inspection"}
                    </button>
                    {confirmInspectionMut.isError && (
                      <p className="cr-error">{apiMsg(confirmInspectionMut.error) || "Failed to confirm inspection. Please try again."}</p>
                    )}
                  </div>
 
                  {/* Waiting charges */}
                  {raw.amountOfRtoC > 0 && (
                    <div className="cr-info-note" style={{ marginTop: 8 }}>
                      Waiting charges: ₹{Number(raw.amountOfRtoC).toLocaleString("en-IN")}
                    </div>
                  )}
                </SubStep>
 
                {/* OTP box — show once inspection confirmed, hide once OTP done */}
                {raw.approvedRightlyMeasured && !raw.otpByCustomer && displayOtp && (
                  <div className="cr-otp-display" style={{ marginLeft: 28, marginBottom: 10 }}>
                    <div className="cr-otp-label">OTP for driver</div>
                    <div className="cr-otp-value">{displayOtp}</div>
                    <div className="cr-otp-hint">
                      Share with driver — they enter it on their app
                    </div>
                  </div>
                )}
 
                {/* Sub-step B: OTP verification */}
                <SubStep
                  number="B"
                  title="OTP verification"
                  sub="Driver enters OTP to confirm vehicle handover"
                  locked={!raw.approvedRightlyMeasured && status !== "VEHICLE_INSPECTED"}
                  done={!!raw.otpByCustomer}
                  doneLabel="OTP verified — vehicle handed over"
                  doneBy={raw.otpByCustomer ? "Verified" : null}
                >
                  <div className="cr-action-row" style={{ alignItems: "flex-end", gap: 8 }}>
                    <div>
                      <div className="cr-form-label" style={{ marginBottom: 4 }}>
                        Enter OTP to verify
                      </div>
                      <input
                        className="cr-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="4-digit OTP"
                        value={otpInput}
                        onChange={e =>
                          setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4))
                        }
                        style={{ width: 120 }}
                      />
                    </div>
                    <button
                      className="cr-btn cr-btn--primary"
                      disabled={otpInput.length !== 4 || confirmOTPMut.isPending}
                      onClick={() =>
                        confirmOTPMut.mutate(otpInput, {
                          onSuccess: () => setOtpInput(""),
                        })
                      }
                    >
                      {confirmOTPMut.isPending
                        ? <span className="qs-btn-spinner" />
                        : <CheckSmIcon />}
                      {confirmOTPMut.isPending ? "Verifying…" : "Verify OTP"}
                    </button>
                    {confirmOTPMut.isError && (
                      <p className="cr-error">
                        {confirmOTPMut.error?.response?.data?.message || "Wrong OTP. Try again."}
                      </p>
                    )}
                  </div>
                </SubStep>
 
                <div className="cr-action-row" style={{ marginTop: 8 }}>
                  <button className="cr-btn cr-btn--secondary" onClick={onTrack}>
                    <MapPinSmIcon /> Live track
                  </button>
                </div>
              </div>
            )}
 
            {/* ── OTP_VERIFIED → WORKING ── */}
            {status === "OTP_VERIFIED" && (
              <div className="cr-action-row">
                <button
                  className="cr-btn cr-btn--primary"
                  disabled={statusMutation.isPending}
                  onClick={() => statusMutation.mutate("WORKING")}
                >
                  {statusMutation.isPending ? <span className="qs-btn-spinner" /> : <TruckSmIcon />}
                  {statusMutation.isPending ? "Updating…" : "Mark driver working / en route to drop"}
                </button>
                <button className="cr-btn cr-btn--secondary" onClick={onTrack}>
                  <MapPinSmIcon /> Live track
                </button>
                {statusMutation.isError && (
                  <p className="cr-error">{apiMsg(statusMutation.error) || "Failed to update status. Please try again."}</p>
                )}
              </div>
            )}
 
            {/* ── WORKING → DONE ── */}
            {status === "WORKING" && (
              <div className="cr-action-row">
                <button
                  className="cr-btn cr-btn--primary"
                  disabled={confirmDoneMut.isPending}
                  onClick={() => confirmDoneMut.mutate()}
                >
                  {confirmDoneMut.isPending
                    ? <span className="qs-btn-spinner" />
                    : <CheckSmIcon />}
                  {confirmDoneMut.isPending ? "Closing case…" : "Confirm job done"}
                </button>
                <button className="cr-btn cr-btn--secondary" onClick={onTrack}>
                  <MapPinSmIcon /> Live track
                </button>
                {confirmDoneMut.isError && (
                  <p className="cr-error">{apiMsg(confirmDoneMut.error) || "Failed to close case. Try again."}</p>
                )}
              </div>
            )}
 
            {/* ── DONE ── */}
            {status === "DONE" && (
              <div className="cr-completed-tag">
                <CheckSmIcon /> Job completed
              </div>
            )}
 
          </div>
        </>
      )}
    </CRPhase>
  );
}

function SubStep({ number, title, sub, done, doneLabel, doneBy, locked, children }) {
  const dotClass = done   ? "cr-substep-dot--done"
                 : locked ? "cr-substep-dot--locked"
                 :          "cr-substep-dot--active";
  return (
    <div className={`cr-substep ${locked ? "cr-substep--locked" : ""} ${done ? "cr-substep--done" : ""}`}>
      <div className="cr-substep-header">
        <div className={`cr-substep-dot ${dotClass}`}>
          {done ? <CheckSmIcon /> : number}
        </div>
        <div className="cr-substep-info">
          <div className="cr-substep-title">{title}</div>
          <div className="cr-substep-sub">
            {done && doneLabel ? doneLabel : sub}
          </div>
          {done && doneBy && (
            <div className="cr-substep-doneby">{doneBy}</div>
          )}
        </div>
      </div>
      {!done && !locked && (
        <div className="cr-substep-body">{children}</div>
      )}
    </div>
  );
}


// ─── Cancel button ────────────────────────────────────────────────────────────

function CancelButton({ requestCode }) {
  const mutation = useUpdateQSStatus(requestCode);
  return (
    <button
      className="wd-btn qs-btn-danger"
      onClick={() =>
        window.confirm("Cancel this service request?") &&
        mutation.mutate("CANCELLED")
      }
    >
      <XSmIcon /> Cancel request
    </button>
  );
}

// ─── Phase wrapper ────────────────────────────────────────────────────────────

function CRPhase({ number, title, state, defaultOpen, badge, children }) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  const dotClass = {
    done:   "cr-phase-dot--done",
    active: "cr-phase-dot--active",
    locked: "cr-phase-dot--locked",
  }[state] || "";

  const badgeClass = {
    done:   "cr-phase-badge--done",
    active: "cr-phase-badge--active",
    locked: "cr-phase-badge--pending",
  }[state] || "cr-phase-badge--pending";

  return (
    <div className={`cr-phase cr-phase--${state}`}>
      <div
        className={`cr-phase-header ${open ? "cr-phase-header--open" : ""}`}
        onClick={() => setOpen(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && setOpen(v => !v)}
      >
        <div className={`cr-phase-dot ${dotClass}`}>
          {state === "done" ? <CheckSmIcon /> : number}
        </div>
        <span className="cr-phase-title" dangerouslySetInnerHTML={{ __html: title }} />
        {badge && (
          <span className={`cr-phase-badge ${badgeClass}`}>{badge}</span>
        )}
        <span className={`cr-phase-chevron ${open ? "cr-phase-chevron--open" : ""}`}>
          <ChevronDownIcon />
        </span>
      </div>
      {open && (
        <div className="cr-phase-body">{children}</div>
      )}
    </div>
  );
}

// ─── Photo strip ──────────────────────────────────────────────────────────────

function PhotoStrip({ photos }) {
  const slots = [
    { key: "front_left",  label: "Front left"  },
    { key: "front_right", label: "Front right" },
    { key: "rear_left",   label: "Rear left"   },
    { key: "rear_right",  label: "Rear right"  },
  ];
  const hasAny = slots.some(s => photos[s.key]) ||
    (Array.isArray(photos.additional) && photos.additional.length > 0);
  if (!hasAny) return null;

  return (
    <div className="cr-photo-strip" style={{ gridColumn: "1 / -1" }}>
      <div className="cr-photo-label">Accident photos</div>
      <div className="cr-photo-row">
        {slots.map(s => (
          photos[s.key] ? (
            <a key={s.key} href={photos[s.key]} target="_blank" rel="noopener noreferrer"
              className="cr-photo-thumb-wrap" title={s.label}>
              <img src={photos[s.key]} alt={s.label} className="cr-photo-thumb" />
              <span className="cr-photo-thumb-label">{s.label}</span>
            </a>
          ) : (
            <div key={s.key} className="cr-photo-thumb-wrap cr-photo-thumb-wrap--empty">
              <span className="cr-photo-thumb-label">{s.label}</span>
            </div>
          )
        ))}
        {Array.isArray(photos.additional) && photos.additional.map((url, i) => (
          <a key={`add-${i}`} href={url} target="_blank" rel="noopener noreferrer"
            className="cr-photo-thumb-wrap" title={`Extra ${i + 1}`}>
            <img src={url} alt={`Extra ${i + 1}`} className="cr-photo-thumb" />
            <span className="cr-photo-thumb-label">Extra {i + 1}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function DetailField({ label, value, full }) {
  return (
    <div className={`qs-detail-field ${full ? "qs-detail-field--full" : ""}`}>
      <div className="qs-detail-label">{label}</div>
      <div className="qs-detail-value">{value || "—"}</div>
    </div>
  );
}

function FormField({ label, placeholder, children }) {
  return (
    <div className="cr-form-field">
      <label className="cr-form-label">{label}</label>
      {children}
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function CheckSmIcon()    { return <svg width="9"  height="9"  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"   strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function XSmIcon()        { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function ChevronDownIcon(){ return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>; }
function TruckSmIcon()    { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>; }
function MapPinSmIcon()   { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function VendorIcon()     { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>; }
function DriverIcon()     { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>; }
function ReuploadIcon()  { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>; }
function CameraSmIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>; }
function PlusIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function FastagIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
      <line x1="5" y1="10" x2="11" y2="10"/>
    </svg>
  );
}
function CashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  );
}

function ClockPayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}